import React from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export function Input({
  label,
  error,
  hint,
  leftIcon,
  rightIcon,
  className = '',
  id,
  ...props
}: InputProps) {
  const inputId = id || `input-${Math.random().toString(36).slice(2, 9)}`;
  const { themeName } = useTheme();
  const isCommand = themeName === 'command';

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            'block text-sm font-medium text-text-light mb-1.5',
            isCommand && 'text-xs font-mono uppercase tracking-wider'
          )}
        >
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
            {leftIcon}
          </div>
        )}
        <input
          id={inputId}
          className={cn(
            'w-full bg-surface-800 border rounded-lg px-4 py-2.5 text-text-primary',
            'placeholder:text-text-muted',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'transition-all duration-150',
            leftIcon && 'pl-10',
            rightIcon && 'pr-10',
            error ? 'border-danger-500' : 'border-surface-500 hover:border-surface-400',
            isCommand && 'rounded font-body',
            className
          )}
          {...props}
        />
        {rightIcon && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary">
            {rightIcon}
          </div>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-danger-400">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-sm text-text-muted">{hint}</p>}
    </div>
  );
}

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export function Textarea({
  label,
  error,
  hint,
  className = '',
  id,
  ...props
}: TextareaProps) {
  const inputId = id || `textarea-${Math.random().toString(36).slice(2, 9)}`;
  const { themeName } = useTheme();
  const isCommand = themeName === 'command';

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            'block text-sm font-medium text-text-light mb-1.5',
            isCommand && 'text-xs font-mono uppercase tracking-wider'
          )}
        >
          {label}
        </label>
      )}
      <textarea
        id={inputId}
        className={cn(
          'w-full bg-surface-800 border rounded-lg px-4 py-2.5 text-text-primary',
          'placeholder:text-text-muted',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          'transition-all duration-150 resize-none',
          error ? 'border-danger-500' : 'border-surface-500 hover:border-surface-400',
          isCommand && 'rounded font-body',
          className
        )}
        {...props}
      />
      {error && <p className="mt-1.5 text-sm text-danger-400">{error}</p>}
      {hint && !error && <p className="mt-1.5 text-sm text-text-muted">{hint}</p>}
    </div>
  );
}
